/* eslint-disable no-magic-numbers */
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import clsx from 'clsx';

import Icon from 'carbon-react/lib/components/icon';

import styles from './top-bar-menu.scss';
import { useOutsideClick } from '@futrli/shared-services';

interface Props<T> {
  label: ReactNode;
  left?: number | string;
  name: string;
  onChange: (value: T) => void;
  options: { label: string; value: T; divider?: boolean }[];
  value?: T;
}
export const TopBarMenu = <T,>({ label, left = 0, name, onChange, options, value }: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (newValue: T) => {
      if (newValue !== value) {
        setIsOpen(false);
        onChange(newValue);
      }
    },
    [onChange, value]
  );

  const handleOutsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleTabClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  useOutsideClick(document.body, containerRef.current, handleOutsideClick);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={clsx(styles.tab)} onClick={handleTabClick}>
        {label}
        {isOpen ? <Icon type="caret_up" /> : <Icon type="caret_down" />}
      </div>

      {isOpen && (
        <aside aria-label={name} className={clsx(styles.menuContainer)} style={{ left }}>
          <ul className={styles.optionsList}>
            {options.map(o => (
              <li
                key={`${o.value}-${o.label}`}
                className={clsx(styles.dropdownItem, {
                  [styles.active]: o.value === value,
                  [styles.divider]: o.divider
                })}
                onClick={() => handleChange(o.value)}
              >
                {o.label}
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
};
